import React, {useCallback, useRef} from 'react';

interface AngleDialProps {
    value: number; // 0–360, 0 = North, clockwise
    onChange: (value: number) => void;
    size?: number;
}

const AngleDial: React.FC<AngleDialProps> = ({value, onChange, size = 90}) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const isDragging = useRef(false);
    const cx = size / 2;
    const cy = size / 2;
    const outerR = cx - 4;
    const innerR = outerR - 8;

    const getAngleFromEvent = useCallback(
        (e: MouseEvent | React.MouseEvent) => {
            if (!svgRef.current) return 0;
            const rect = svgRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left - cx;
            const y = e.clientY - rect.top - cy;
            // atan2 gives angle from East; add 90 to get angle from North
            let angle = Math.atan2(y, x) * (180 / Math.PI) + 90;
            return ((Math.round(angle) % 360) + 360) % 360;
        },
        [cx, cy]
    );

    const handleMouseDown = useCallback(
        (e: React.MouseEvent<SVGSVGElement>) => {
            e.preventDefault();
            isDragging.current = true;
            onChange(getAngleFromEvent(e));

            const onMove = (e: MouseEvent) => {
                if (isDragging.current) onChange(getAngleFromEvent(e));
            };
            const onUp = () => {
                isDragging.current = false;
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        [getAngleFromEvent, onChange]
    );

    // Convert compass bearing (N=0, CW) → SVG angle (right=0, CW)
    const svgRad = ((value - 90) * Math.PI) / 180;
    const tipX = cx + (outerR - 4) * Math.cos(svgRad);
    const tipY = cy + (outerR - 4) * Math.sin(svgRad);
    const tailX = cx - (innerR - 6) * Math.cos(svgRad);
    const tailY = cy - (innerR - 6) * Math.sin(svgRad);

    // Tick marks: every 30°, major every 90°
    const ticks = Array.from({length: 12}, (_, i) => {
        const a = ((i * 30 - 90) * Math.PI) / 180;
        const major = i % 3 === 0;
        const r1 = outerR;
        const r2 = outerR - (major ? 7 : 4);
        return {
            x1: cx + r1 * Math.cos(a),
            y1: cy + r1 * Math.sin(a),
            x2: cx + r2 * Math.cos(a),
            y2: cy + r2 * Math.sin(a),
            major,
        };
    });

    return (
        <svg
            ref={svgRef}
            width={size}
            height={size}
            style={{cursor: 'pointer', userSelect: 'none', flexShrink: 0, display: 'block'}}
            onMouseDown={handleMouseDown}
        >
            {/* Background */}
            <circle cx={cx} cy={cy} r={outerR} fill="#1e2030" stroke="#4a4f6e" strokeWidth={1.5}/>
            <circle cx={cx} cy={cy} r={innerR} fill="#13151f"/>

            {/* Tick marks */}
            {ticks.map((t, i) => (
                <line
                    key={i}
                    x1={t.x1} y1={t.y1}
                    x2={t.x2} y2={t.y2}
                    stroke={t.major ? '#5a607a' : '#2e334a'}
                    strokeWidth={t.major ? 1.5 : 1}
                />
            ))}

            {/* Cardinal labels */}
            <text x={cx} y={10} textAnchor="middle" fill="#ffffff" fontSize={9} fontWeight="bold">N</text>
            <text x={size - 5} y={cy + 3} textAnchor="middle" fill="#5a607a" fontSize={7}>E</text>
            <text x={cx} y={size - 2} textAnchor="middle" fill="#5a607a" fontSize={7}>S</text>
            <text x={5} y={cy + 3} textAnchor="middle" fill="#5a607a" fontSize={7}>W</text>

            {/* Needle (tail in opposite direction, tip at bearing) */}
            <line x1={tailX} y1={tailY} x2={tipX} y2={tipY}
                  stroke="#ff6600" strokeWidth={2.5} strokeLinecap="round"/>
            <circle cx={tipX} cy={tipY} r={2.5} fill="#ff6600"/>
            {/* Center hub */}
            <circle cx={cx} cy={cy} r={3.5} fill="#ff6600"/>
        </svg>
    );
};

export default AngleDial;
